import type { Ssm5FreezeFrameParameter } from '@packages/domain/ssm5';

import { colors } from '@packages/core/styles';
import { Typography } from '@packages/ui/shared';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@web/components/table';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';

import type { Ssm5FreezeFrameColumn } from '../helpers/freeze-frame-columns';

import { freezeFrameTableWidth } from '../helpers/freeze-frame-columns';
import { freezeFrameValueText } from '../helpers/freeze-frame-values';
import { ssm5TextStyles } from '../ssm5.styles';
import { Ssm5PanelMessage } from './ssm5-panel-message';

/** Props for {@link Ssm5FreezeFramesTable}. */
export type Ssm5FreezeFramesTableProps = {
    /** The captured moments in display order. */
    columns: readonly Ssm5FreezeFrameColumn[];
    /** The PIDs on the displayed page. */
    rows: readonly Ssm5FreezeFrameParameter[];
};

/** The Detect body fill read from the design. */
const DETECT_BODY_COLOR = '#EEF2F6';

/** The table width derived from the number of captured moments. */
type TableStyleProps = {
    /** The CSS width of the table. */
    width: string;
};

const useStyles = createUseStyles({
    area: {
        border: `1px solid ${colors.grayThree}`,
        borderBottom: 0,
        display: 'flex',
        flex: 1,
        flexDirection: 'column',
        margin: '0 24px',
        minHeight: 0,
        minWidth: 0,
    },
    areaWithoutRows: { borderBottom: `1px solid ${colors.grayThree}`, borderRadius: 4, overflow: 'hidden' },
    cell: {
        '&&': {
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
            minHeight: 56,
            padding: 16,
        },
    },
    cellOuter: { '&&': { boxShadow: `inset 0 -1px 0 ${colors.grayThree}` } },
    detectCell: { '&&': { backgroundColor: DETECT_BODY_COLOR, boxShadow: `inset 0 -1px 0 ${colors.blueFour}` } },
    header: {
        '&&': {
            alignItems: 'center',
            boxSizing: 'border-box',
            display: 'flex',
            height: 56,
            padding: 16,
        },
    },
    detectHeader: { '&&': { backgroundColor: colors.blueFour } },
    row: { '&&': { borderBottom: 0 } },
    innerTable: { '&&': { width: ({ width }: TableStyleProps) => width } },
    table: { '&&': { border: 0, minHeight: 0, minWidth: 0 } },
    tableWithoutRows: { '&&': { flex: 'none' } },
});

/**
 * The captured PID values, with sticky headers and PID cells, or the search feedback under the headers.
 *
 * @param props - {@link Ssm5FreezeFramesTableProps}
 * @returns the table area
 */
export function Ssm5FreezeFramesTable({ columns, rows }: Ssm5FreezeFramesTableProps) {
    const classes = useStyles({ width: freezeFrameTableWidth(columns.length + 1) });
    const { t } = useTranslation();
    const texts = { empty: t('ssm5:ffd.values.empty'), invalid: t('ssm5:ffd.values.invalid') };
    const hasNoRows = rows.length === 0;

    return (
        <div className={classNames(classes.area, { [classes.areaWithoutRows]: hasNoRows })}>
            <Table
                className={classNames(classes.table, { [classes.tableWithoutRows]: hasNoRows })}
                tableClassName={classes.innerTable}
                useFixedLayout
            >
                <TableHead>
                    <TableRow>
                        <TableHeader className={classes.header} fixed>
                            <Typography style={ssm5TextStyles.bodySemibold} variant="default">
                                {t('ssm5:ffd.columns.pid')}
                            </Typography>
                        </TableHeader>
                        {columns.map((column) => (
                            <TableHeader
                                className={classNames(classes.header, {
                                    [classes.detectHeader]: column.frame.kind === 'detect',
                                })}
                                key={column.key}
                            >
                                <Typography style={ssm5TextStyles.bodySemibold} variant="default">
                                    {t(column.label.key, { sequence: column.label.sequence })}
                                </Typography>
                            </TableHeader>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow className={classes.row} key={row.name}>
                            <TableCell className={classes.cell} classNameOuter={classes.cellOuter} fixed>
                                <Typography style={ssm5TextStyles.body} variant="default">
                                    {row.name}
                                </Typography>
                            </TableCell>
                            {columns.map((column) => (
                                <TableCell
                                    className={classes.cell}
                                    classNameOuter={classNames(classes.cellOuter, {
                                        [classes.detectCell]: column.frame.kind === 'detect',
                                    })}
                                    key={`${row.name}-${column.key}`}
                                >
                                    <Typography style={ssm5TextStyles.body} variant="default">
                                        {freezeFrameValueText(row.values[column.valueIndex], texts)}
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            {hasNoRows && (
                <Ssm5PanelMessage
                    description={t('ssm5:ffd.noMatch.description')}
                    icon="search"
                    title={t('ssm5:ffd.noMatch.title')}
                    variant="page"
                />
            )}
        </div>
    );
}
